/**
 * Application color palette - Fight Less Win More Brand
 * Comprehensive palette for light and dark modes with semantic naming
 * Primary brand color: #e50c20 (Fight Less Win More Red)
 */

export const colors = {
  light: {
    // ===== PRIMARY BRAND COLORS =====
    brand_primary: "rgba(229, 12, 32, 1)",       // #e50c20 - Main brand red
    brand_primary_dark: "rgba(184, 9, 26, 1)",   // #b8091a - Darker red for hover
    brand_primary_light: "rgba(255, 71, 87, 1)", // #ff4757 - Lighter red for highlights
    brand_primary_subtle: "rgba(255, 229, 231, 1)", // #ffe5e7 - Very light red for backgrounds

    // ===== BACKGROUND COLORS =====
    background_primary: "rgba(255, 255, 255, 1)",   // #ffffff - Main background
    background_secondary: "rgba(248, 249, 250, 1)", // #f8f9fa - Card backgrounds
    background_tertiary: "rgba(233, 236, 239, 1)",  // #e9ecef - Subtle areas
    
    // Legacy backgrounds (preserved for compatibility)
    bgoffwhitecool: "rgba(248, 249, 250, 1)",
    bgoffwhiteneutral: "rgba(248, 249, 250, 1)",
    bgoffwhitewarm: "rgba(250, 250, 251, 1)",
    bgoffwhitebluetint: "rgba(247, 249, 252, 1)",
    natural_light_BG_glacial: "rgba(244, 255, 255, 1)",

    // ===== TEXT COLORS =====
    text_primary: "rgba(26, 29, 41, 1)",         // #1a1d29 - High contrast main text
    text_secondary: "rgba(74, 85, 104, 1)",      // #4a5568 - Secondary text, labels
    text_muted: "rgba(113, 128, 150, 1)",        // #718096 - Disabled text, placeholders
    text_on_red: "rgba(255, 255, 255, 1)",       // #ffffff - Text on red backgrounds
    
    // Legacy text colors (preserved)
    primary_midnight: "rgba(26, 29, 41, 1)",
    locked_primary: "rgba(136, 46, 82, 1)",
    text_primary_light: "rgba(225, 230, 240, 1)",

    // ===== SEMANTIC COLORS =====
    success: "rgba(16, 185, 129, 1)",            // #10b981 - Success states (emerald)
    success_text: "rgba(16, 185, 129, 1)",
    success_container: "rgba(237, 249, 240, 1)",
    
    warning: "rgba(245, 158, 11, 1)",            // #f59e0b - Warning states (amber)
    warning_text: "rgba(245, 158, 11, 1)",
    warning_container: "rgba(255, 246, 224, 1)",
    
    error: "rgba(239, 68, 68, 1)",               // #ef4444 - Error states (red)
    error_text: "rgba(239, 68, 68, 1)",
    error_container: "rgba(255, 237, 238, 1)",
    
    info: "rgba(59, 130, 246, 1)",               // #3b82f6 - Info states (blue)
    info_text: "rgba(59, 130, 246, 1)",
    info_container: "rgba(232, 244, 253, 1)",

    // ===== NEUTRAL/SLATE COLORS =====
    slate_main: "rgba(71, 85, 105, 1)",          // #475569 - Professional slate
    slate_light: "rgba(203, 213, 225, 1)",       // #cbd5e1 - Light slate for borders
    slate_dark: "rgba(51, 65, 85, 1)",           // #334155 - Dark slate
    
    // ===== GREY PALETTE =====
    light_grey: "rgba(176, 176, 176, 1)",
    light_charcoal: "rgba(112, 112, 112, 1)",
    soft_grey: "rgba(106, 106, 106, 1)",
    charcoal: "rgba(68, 68, 68, 1)",
    dark_grey: "rgba(51, 51, 51, 1)",
    jet_black: "rgba(26, 26, 26, 1)",

    // ===== DIVIDERS & BORDERS =====
    divider_line_pale_gray: "rgba(233, 236, 239, 1)",
    divider_line_soft_white: "rgba(244, 247, 251, 1)",
    divider_line_light_gray: "rgba(203, 213, 225, 1)",

    // ===== ACTION COLORS =====
    coolactive: "rgba(229, 12, 32, 0.08)",       // Red-based active state
    coolactive_hover: "rgba(229, 12, 32, 0.12)",
    selected: "rgba(229, 12, 32, 0.16)",
    disabled: "rgba(148, 163, 184, 1)",
    disabledBackground: "rgba(241, 245, 249, 1)",

    // ===== LEGACY COLORS (preserved for compatibility) =====
    secondary_guardian: "rgba(51, 77, 134, 1)",
    tertiary_nautical: "rgba(101, 141, 177, 1)",
    accent_horizon: "rgba(139, 180, 232, 1)",
    hero_blue: "rgba(63, 124, 233, 1)",
    placeholder_gray: "rgba(113, 128, 150, 1)",
    button_tonal_guardian: "rgba(62, 97, 144, 1)",
    input_guardian_light: "rgba(78, 98, 142, 1)",
    custom_locked_primary: "rgba(26, 29, 41, 1)",
    constant_black: "rgba(0, 0, 0, 1)"
  },

  dark: {
    // ===== PRIMARY BRAND COLORS =====
    brand_primary: "rgba(229, 12, 32, 1)",       // #e50c20 - Main brand red (same in dark mode)
    brand_primary_dark: "rgba(184, 9, 26, 1)",   // #b8091a - Darker red for hover
    brand_primary_light: "rgba(255, 71, 87, 1)", // #ff4757 - Lighter red for highlights
    brand_primary_subtle: "rgba(45, 10, 14, 1)", // #2d0a0e - Dark red tint for subtle backgrounds

    // ===== BACKGROUND COLORS =====
    background_primary: "rgba(18, 18, 18, 1)",    // #121212 - Pure neutral black (Material Design dark)
    background_secondary: "rgba(28, 28, 28, 1)",  // #1c1c1c - Elevated surfaces, cards
    background_tertiary: "rgba(38, 38, 38, 1)",   // #262626 - Borders, dividers
    
    // Legacy backgrounds (preserved for compatibility)
    bgoffwhitecool: "rgba(28, 28, 28, 1)",
    bgoffwhiteneutral: "rgba(28, 28, 28, 1)",
    bgoffwhitewarm: "rgba(22, 22, 22, 1)",
    bgoffwhitebluetint: "rgba(24, 24, 24, 1)",
    natural_light_BG_glacial: "rgba(20, 20, 20, 1)",

    // ===== TEXT COLORS =====
    text_primary: "rgba(241, 245, 249, 1)",      // #f1f5f9 - Off-white (easier than pure white)
    text_secondary: "rgba(203, 213, 225, 1)",    // #cbd5e1 - Secondary text
    text_muted: "rgba(148, 163, 184, 1)",        // #94a3b8 - Disabled text, placeholders
    text_on_red: "rgba(255, 255, 255, 1)",       // #ffffff - Text on red backgrounds
    
    // Legacy text colors (preserved)
    primary_midnight: "rgba(241, 245, 249, 1)",
    locked_primary: "rgba(200, 120, 150, 1)",
    text_primary_light: "rgba(225, 230, 240, 1)",

    // ===== SEMANTIC COLORS =====
    success: "rgba(52, 211, 153, 1)",            // #34d399 - Brighter emerald for dark mode
    success_text: "rgba(52, 211, 153, 1)",
    success_container: "rgba(15, 40, 20, 1)",
    
    warning: "rgba(251, 191, 36, 1)",            // #fbbf24 - Brighter amber for dark mode
    warning_text: "rgba(251, 191, 36, 1)",
    warning_container: "rgba(50, 40, 10, 1)",
    
    error: "rgba(255, 107, 107, 1)",             // #ff6b6b - Softer red for dark backgrounds
    error_text: "rgba(255, 107, 107, 1)",
    error_container: "rgba(50, 20, 20, 1)",
    
    info: "rgba(96, 165, 250, 1)",               // #60a5fa - Brighter blue for dark mode
    info_text: "rgba(96, 165, 250, 1)",
    info_container: "rgba(15, 30, 50, 1)",

    // ===== NEUTRAL/SLATE COLORS =====
    slate_main: "rgba(100, 116, 139, 1)",        // #64748b - Lighter slate for dark mode
    slate_light: "rgba(51, 65, 85, 1)",          // #334155 - Dark slate for borders
    slate_dark: "rgba(148, 163, 184, 1)",        // #94a3b8 - Lighter slate

    // ===== GREY PALETTE =====
    light_grey: "rgba(100, 100, 100, 1)",
    light_charcoal: "rgba(160, 160, 160, 1)",
    soft_grey: "rgba(180, 180, 180, 1)",
    charcoal: "rgba(200, 200, 200, 1)",
    dark_grey: "rgba(220, 220, 220, 1)",
    jet_black: "rgba(240, 240, 240, 1)",

    // ===== DIVIDERS & BORDERS =====
    divider_line_pale_gray: "rgba(48, 48, 48, 1)",    // #303030 - Neutral dark dividers
    divider_line_soft_white: "rgba(56, 56, 56, 1)",   // #383838 - Slightly lighter
    divider_line_light_gray: "rgba(64, 64, 64, 1)",   // #404040 - Light borders

    // ===== ACTION COLORS =====
    coolactive: "rgba(229, 12, 32, 0.12)",       // Red-based active state for dark mode
    coolactive_hover: "rgba(229, 12, 32, 0.16)",
    selected: "rgba(229, 12, 32, 0.20)",
    disabled: "rgba(100, 100, 100, 1)",          // #646464 - Neutral disabled
    disabledBackground: "rgba(38, 38, 38, 1)",   // #262626 - Matches tertiary bg

    // ===== LEGACY COLORS (preserved for compatibility) =====
    secondary_guardian: "rgba(100, 130, 190, 1)",
    tertiary_nautical: "rgba(140, 180, 220, 1)",
    accent_horizon: "rgba(70, 130, 200, 1)",
    hero_blue: "rgba(100, 150, 240, 1)",
    placeholder_gray: "rgba(148, 163, 184, 1)",
    button_tonal_guardian: "rgba(62, 97, 144, 1)",
    input_guardian_light: "rgba(120, 145, 200, 1)",
    custom_locked_primary: "rgba(241, 245, 249, 1)",
    constant_black: "rgba(0, 0, 0, 1)"
  },
};

export default colors;
